import { JtmsDriveAppProperties } from "./drive-app-properties";

export const MIME_TYPE_FOLDER = "application/vnd.google-apps.folder";
export const MIME_TYPE_SHORTCUT = "application/vnd.google-apps.shortcut";
export const DRIVE_FOLDER_URL = "https://drive.google.com/drive/folders/";
export const DRIVE_FILE_URL = "https://drive.google.com/file/d/";
export const DOCS_FILE_URL = "https://docs.google.com/document/d/";

export function folderUrlFromId(folderId: string): string {
  return `${DRIVE_FOLDER_URL}${folderId}`;
}

export function driveFileUrlFromId(fileId: string): string {
  return `${DRIVE_FILE_URL}${fileId}`;
}

export function docUrlFromId(fileId: string): string {
  return `${DOCS_FILE_URL}${fileId}`;
}

export function docPreviewUrlFromId(fileId: string): string {
  return `${DOCS_FILE_URL}${fileId}/preview`;
}

export interface FileProperties {
  documentType?: string;
  purchaseOrder?: string;
  documentDate?: Date;
  description?: string;
  jobNumber?: string;
  buildNumber?: string;
  serialNumbers?: string;
  traceabilityCheck?: boolean;
  traceNumber?: string;
  /** Comma-separated list of field names still AI-suggested-and-unedited at submit time. */
  aiAssisted?: string;
  /**
   * NDE-report-specific fields, each persisted independently so they round-trip without
   * re-parsing `description` (which stays the composite `${company} Inspection, ${description},
   * ..., Invoice ${invoice}` string used for the Drive file name and legacy file compatibility).
   */
  inspectionCompany?: string;
  inspectionInvoice?: string;
  inspectionDescription?: string;
}

export interface ShortcutDetails {
  targetId: string;
  targetMimeType: string;
  targetResourceKey: string;
}

export class FileInfo {
  AppProperties!: JtmsDriveAppProperties;
  Blob!: File;
  Id!: string;
  MimeType!: string;
  ModifiedTime!: Date;
  Name!: string;
  Progress!: number;
  Size!: string;
  ThumbnailLink!: string;
  Url!: string;
  Version!: string;
  DownloadLink!: string;
  ExportLinks: { [mimeType: string]: string } | undefined;
  Properties!: FileProperties;
  Parents: string[] | undefined;
  createdTime!: Date;
  shortcutDetails!: ShortcutDetails;

  static fromGoogleFile(file: gapi.client.drive.File): FileInfo {
    const fileInfo = new FileInfo();
    // id, mimeType, name, modifiedTime and createdTime are always requested via the
    // `fields` parameter on every Drive API call that constructs a FileInfo, so they
    // are guaranteed to be present even though the generated gapi types mark them optional.
    fileInfo.Id = file.id!;
    fileInfo.MimeType = file.mimeType!;
    fileInfo.ModifiedTime = new Date(file.modifiedTime!);
    fileInfo.Name = file.name!;
    fileInfo.Size = file.size!;
    fileInfo.ThumbnailLink = file.thumbnailLink!;
    fileInfo.Url = file.webViewLink!;
    fileInfo.Version = file.version!;
    fileInfo.DownloadLink = file.webContentLink!;
    fileInfo.ExportLinks = file.exportLinks;
    fileInfo.AppProperties = file.appProperties || {};
    fileInfo.Properties = (file.properties || {}) as FileProperties;
    fileInfo.Parents = file.parents;
    fileInfo.createdTime = new Date(file.createdTime!);
    fileInfo.shortcutDetails = (file.shortcutDetails || {}) as ShortcutDetails;
    return fileInfo;
  }

  public get Icon(): string {
    if (this.IsFolder) {
      return "folder";
    } else {
      return "description";
    }
  }

  /**
   * A shortcut-to-folder's own `webViewLink` opens the shortcut file, not the folder it
   * points to, so folder-typed entries need their link rebuilt from the target folder id.
   */
  public get LinkUrl(): string {
    if (this.IsFolder) {
      const targetId = this.IsShortcutToFolder ? this.shortcutDetails.targetId : this.Id;
      return folderUrlFromId(targetId);
    }
    return this.Url;
  }

  public get IsShortcut(): boolean {
    return this.MimeType === MIME_TYPE_SHORTCUT;
  }

  public get IsShortcutToFolder(): boolean {
    return this.IsShortcut && this.shortcutDetails?.targetMimeType === MIME_TYPE_FOLDER;
  }

  public get IsFolder(): boolean {
    // Check if it's a folder directly
    if (this.MimeType === MIME_TYPE_FOLDER) {
      return true;
    }
    // Check if it's a shortcut pointing to a folder
    if (this.IsShortcutToFolder) {
      return true;
    }
    return false;
  }

  public get ModifiedTimeText(): string {
    return `${this.ModifiedTime.getDate()}.${this.ModifiedTime.getMonth() + 1}.${this.ModifiedTime.getFullYear()}`;
  }
}
